// 🎃 10-07 pumpkins (P6): the carving table's PURE layer. Its other half is the backend's validator,
// justsomeguy-backend/src/ChatGame/pumpkinCuts.js: pumpkinCarveParity.test.js requires that module and proves
// that everything this file builds passes it UNCHANGED (what you see on the table is what the porch stores).
// The data model and the feel are the 09-28 mockup Nina approved ("Pumpkin feel is fine"):
// ~/Desktop/yt-renders/room-art-masters-2026-09-15/pumpkin-carving-mockup-2026-09-28/pumpkin-carving.html,
// frozen copy https://claude.ai/artifact/44e8LEB484m5isg1ZaobV8 (HOUSE_OCTOBER_UIUX_BATCH_SPEC_2026_10_01.md §3.13).
//
// A pumpkin = a name (≤ 24 code points) + ORDERED cuts on a 600 x 600 canvas:
//   { k: 'shape', p: [[x, y], ...] }           a loop traced all the way round: a hole
//   { k: 'line', w: 9 | 20, p: [[x, y], ...] } an open line: a scored groove (the fine knife or the big scoop)
// The order IS the carving: the big view's replay draws them in order.

export const CANVAS = 600;
export const KNIFE_FINE = 9;
export const KNIFE_SCOOP = 20;
export const KNIFE_WIDTHS = Object.freeze([KNIFE_FINE, KNIFE_SCOOP]);
// the backend's caps: over one = REFUSED there, so the page simplifies to them before it keeps a cut
export const MAX_CUTS = 60;
export const MAX_POINTS_PER_CUT = 300;
export const MAX_POINTS_TOTAL = 2400;
export const NAME_MAX = 24;
export const DEFAULT_NAME = 'Unnamed';

// The body: five overlapping lobes (the mockup's; the carving pumpkin C painting was drawn to them,
// carve-ref/overlay-ABC.png). Cuts are clipped to the lobes; the stem is never cut.
export const CY = 345;
export const LOBES = Object.freeze([
  Object.freeze({ x: 128, rx: 98, ry: 172 }), Object.freeze({ x: 472, rx: 98, ry: 172 }),
  Object.freeze({ x: 205, rx: 116, ry: 194 }), Object.freeze({ x: 395, rx: 116, ry: 194 }),
  Object.freeze({ x: 300, rx: 120, ry: 206 }),
]);

// The mockup's stroke rules.
export const MIN_STEP = 3;           // a new point every 3 canvas px
export const LIVE_MAX = 800;         // a live stroke stops growing here
export const TAP_LEN = 6;            // shorter than this (or under 3 points) = a tap = a round hole
export const CLOSE_GAP = 34;         // ends this close …
export const CLOSE_MIN_LEN = 90;     // … on a stroke this long = a closed loop = a hole
export const MIRROR_DEADZONE = 18;   // a cut centered on the middle line is not mirrored
export const SIMPLIFY_TOLERANCE = 1; // canvas px: keeps the look, drops the hand's noise

// The backend's own refusal words (pumpkinCuts.COPY, parity-pinned).
export const COPY = Object.freeze({
  empty: 'Carve something first.',
  too_many: `That is a lot of carving. Keep it under ${MAX_CUTS} cuts.`,
  too_long: 'One of those cuts is too long. Try a shorter stroke.',
  too_much: 'That pumpkin has too many points. Simplify a little.',
  bad: "That carving didn't come through. Try again.",
});

// ── geometry (the mockup's helpers) ──────────────────────────────────────────
export function ellipsePoly(cx, cy, rx, ry, rot = 0, n = 28) {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const x = Math.cos(t) * rx;
    const y = Math.sin(t) * ry;
    pts.push([cx + x * Math.cos(rot) - y * Math.sin(rot), cy + x * Math.sin(rot) + y * Math.cos(rot)]);
  }
  return pts;
}
export function almond(cx, cy, rx, ry, rot) {
  const pts = [];
  const n = 26;
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const x = Math.cos(t) * rx;
    const y = Math.sin(t) * ry * Math.pow(Math.abs(Math.sin(t)), 0.35);
    pts.push([cx + x * Math.cos(rot) - y * Math.sin(rot), cy + x * Math.sin(rot) + y * Math.cos(rot)]);
  }
  return pts;
}
export const mirrorPts = (pts) => pts.map(([x, y]) => [CANVAS - x, y]);
const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
export function pathLength(pts) {
  let len = 0;
  for (let i = 1; i < pts.length; i++) len += dist(pts[i], pts[i - 1]);
  return len;
}

// ── stencils (the mockup's five; the same shape language the residents' AI doodles use) ──
export const STENCILS = Object.freeze({
  classic: () => [
    { k: 'shape', p: [[198, 280], [258, 280], [228, 222]] },
    { k: 'shape', p: [[342, 280], [402, 280], [372, 222]] },
    { k: 'shape', p: [[284, 334], [316, 334], [300, 302]] },
    { k: 'shape', p: [[186, 372], [228, 392], [250, 376], [272, 396], [300, 380], [328, 396], [350, 376], [372, 392], [414, 372], [392, 420], [344, 446], [300, 452], [256, 446], [208, 420]] },
  ],
  cat: () => [
    { k: 'shape', p: almond(228, 268, 40, 20, 0.28) },
    { k: 'shape', p: almond(372, 268, 40, 20, -0.28) },
    { k: 'shape', p: [[286, 312], [314, 312], [300, 330]] },
    { k: 'line', w: 9, p: [[258, 356], [279, 372], [300, 358], [321, 372], [342, 356]] },
    { k: 'line', w: 7, p: [[246, 334], [176, 322]] }, { k: 'line', w: 7, p: [[246, 346], [174, 354]] },
    { k: 'line', w: 7, p: [[354, 334], [424, 322]] }, { k: 'line', w: 7, p: [[354, 346], [426, 354]] },
  ],
  bat: () => [
    { k: 'shape', p: [[300, 300], [288, 280], [284, 302], [262, 292], [238, 276], [200, 280], [166, 298], [196, 304], [214, 322], [236, 314], [256, 332], [276, 326], [300, 356], [324, 326], [344, 332], [364, 314], [386, 322], [404, 304], [434, 298], [400, 280], [362, 276], [338, 292], [316, 302], [312, 280]] },
  ],
  surprised: () => [
    { k: 'shape', p: ellipsePoly(232, 262, 30, 34, 0) },
    { k: 'shape', p: ellipsePoly(368, 262, 30, 34, 0) },
    { k: 'shape', p: ellipsePoly(300, 402, 36, 46, 0) },
  ],
  toothy: () => [
    { k: 'shape', p: [[196, 248], [266, 274], [204, 294]] },
    { k: 'shape', p: [[404, 248], [334, 274], [396, 294]] },
    { k: 'shape', p: [[178, 360], [238, 380], [254, 380], [254, 408], [276, 408], [276, 390], [324, 390], [324, 408], [346, 408], [346, 380], [362, 380], [422, 360], [402, 398], [352, 432], [300, 444], [248, 432], [198, 398]] },
  ],
});
/** The stencil buttons, in the mockup's order and words. */
export const STENCIL_BUTTONS = Object.freeze([
  ['classic', 'Classic'], ['cat', 'Cat'], ['bat', 'Bat'], ['surprised', 'Oh no'], ['toothy', 'Teeth'],
]);

// ── the backend's normalization, mirrored (so the drawing IS the stored cut) ──
/** A coordinate: a finite NUMBER, rounded and clamped to the canvas, or null. */
export function coord(v) {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return Math.max(0, Math.min(CANVAS, Math.round(v)));
}
/** The knife a width means: 9 or 20 (a tie takes the finer), null when it is no width. */
export function knife(w) {
  if (typeof w !== 'number' || !Number.isFinite(w) || w < 1 || w > 40) return null;
  return KNIFE_WIDTHS.reduce((best, k) => (Math.abs(k - w) < Math.abs(best - w) ? k : best), KNIFE_WIDTHS[0]);
}
/** One cut, rounded + clamped + its knife snapped (the backend stores exactly this), or null. */
export function normalizeCut(c) {
  if (!c || (c.k !== 'shape' && c.k !== 'line') || !Array.isArray(c.p)) return null;
  const p = [];
  for (const pt of c.p) {
    if (!Array.isArray(pt) || pt.length !== 2) return null;
    const x = coord(pt[0]);
    const y = coord(pt[1]);
    if (x === null || y === null) return null;
    p.push([x, y]);
  }
  if (p.length < (c.k === 'shape' ? 3 : 2)) return null;
  if (c.k === 'shape') return { k: 'shape', p };
  const w = c.w === undefined || c.w === null ? KNIFE_FINE : knife(c.w);
  return w === null ? null : { k: 'line', w, p };
}

// ── simplification (to the caps, never a blind trim) ─────────────────────────
function perpDist(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (!len) return dist(p, a);
  return Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]) / len;
}
/** Ramer-Douglas-Peucker on a polyline (iterative: a long stroke never blows the stack). */
export function simplify(pts, tol = SIMPLIFY_TOLERANCE) {
  if (!Array.isArray(pts) || pts.length < 3) return Array.isArray(pts) ? pts.slice() : [];
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let far = -1;
    let fd = tol;
    for (let i = s + 1; i < e; i++) {
      const d = perpDist(pts[i], pts[s], pts[e]);
      if (d > fd) { fd = d; far = i; }
    }
    if (far >= 0) { keep[far] = 1; stack.push([s, far], [far, e]); }
  }
  return pts.filter((_, i) => keep[i]);
}
/** At most `max` points: simplify, then raise the tolerance until it fits (bounded); a last resort keeps
 *  every n-th point (the ends kept). The shape stays recognisable; nothing is cut off its end. */
export function fitPoints(pts, max = MAX_POINTS_PER_CUT, tol = SIMPLIFY_TOLERANCE) {
  let out = simplify(pts, tol);
  for (let t = tol * 1.6, i = 0; out.length > max && i < 14; t *= 1.6, i++) out = simplify(pts, t);
  if (out.length > max) {
    const step = (out.length - 1) / (max - 1);
    out = Array.from({ length: max }, (_, i) => out[Math.round(i * step)]);
  }
  return out;
}

// ── strokes → cuts (the mockup's endStroke) ──────────────────────────────────
/** A finished stroke (canvas points) → its cut, normalized: a tap = a round hole the knife's size, a closed
 *  loop = a hole, anything else = a scored line. */
export function strokeToCut(points, knifeW = KNIFE_FINE) {
  const pts = (Array.isArray(points) ? points : []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (!pts.length) return null;
  const len = pathLength(pts);
  const w = knife(knifeW) || KNIFE_FINE;
  if (pts.length < 3 || len < TAP_LEN) {
    return normalizeCut({ k: 'shape', p: ellipsePoly(pts[0][0], pts[0][1], w * 0.9, w * 0.9, 0, 14) });
  }
  const closed = dist(pts[0], pts[pts.length - 1]) < CLOSE_GAP && len > CLOSE_MIN_LEN;
  const p = fitPoints(pts, MAX_POINTS_PER_CUT);
  return normalizeCut(closed ? { k: 'shape', p } : { k: 'line', w, p });
}
/** The mirror twin of a cut, or null when the cut sits on the middle line (the mockup's dead zone). */
export function mirrorOf(cut) {
  if (!cut || !Array.isArray(cut.p) || !cut.p.length) return null;
  const mx = cut.p.reduce((s, p) => s + p[0], 0) / cut.p.length;
  if (Math.abs(mx - CANVAS / 2) <= MIRROR_DEADZONE) return null;
  return normalizeCut(cut.k === 'line' ? { k: 'line', w: cut.w, p: mirrorPts(cut.p) } : { k: 'shape', p: mirrorPts(cut.p) });
}
/** One finished stroke → the cuts it adds: [cut] or [cut, its mirror]. [] when nothing came through. */
export function cutsForStroke(points, { knife: knifeW = KNIFE_FINE, mirror = true } = {}) {
  const cut = strokeToCut(points, knifeW);
  if (!cut) return [];
  const twin = mirror ? mirrorOf(cut) : null;
  return twin ? [cut, twin] : [cut];
}
/** A stencil's cuts, normalized (its 7 px whiskers snap to the fine knife, as the porch will store them). */
export function stencilCuts(id) {
  const make = STENCILS[id];
  return make ? make().map(normalizeCut).filter(Boolean) : [];
}
/** Whether `extra` more cuts still fit the cut cap. */
export const roomFor = (cuts, extra = 1) => (Array.isArray(cuts) ? cuts.length : 0) + extra <= MAX_CUTS;
export const pointCount = (cuts) => (Array.isArray(cuts) ? cuts.reduce((n, c) => n + ((c && c.p && c.p.length) || 0), 0) : 0);

// ── the name (the backend's cleanName, mirrored) ─────────────────────────────
export function cleanName(raw) {
  try {
    let s = typeof raw === 'string' ? raw.slice(0, NAME_MAX * 8) : '';
    s = s.normalize('NFKC')
      .replace(/[\p{Cc}\p{Cf}\p{Cs}\p{Co}]/gu, '')
      .replace(/[<>]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    s = Array.from(s).slice(0, NAME_MAX).join('').trim();
    return s || DEFAULT_NAME;
  } catch (e) {
    return DEFAULT_NAME;
  }
}

// ── the carve body ───────────────────────────────────────────────────────────
/** What "Put it on the porch" sends: { ok: true, body: { name, cuts } }, or { ok: false, reason, message } with
 *  the backend's own words. Over the total point cap it simplifies every cut harder first (bounded). */
export function carvePayload({ name, cuts } = {}) {
  const list = Array.isArray(cuts) ? cuts : [];
  if (!list.length) return { ok: false, reason: 'empty', message: COPY.empty };
  if (list.length > MAX_CUTS) return { ok: false, reason: 'too_many', message: COPY.too_many };
  let norm = list.map(normalizeCut);
  if (norm.some((c) => !c)) return { ok: false, reason: 'bad', message: COPY.bad };
  for (let t = SIMPLIFY_TOLERANCE * 2, i = 0; pointCount(norm) > MAX_POINTS_TOTAL && i < 8; t *= 1.6, i++) {
    // a cut that would collapse below its minimum keeps its last good version: never a silent drop
    norm = norm.map((c) => normalizeCut({ ...c, p: fitPoints(c.p, MAX_POINTS_PER_CUT, t) }) || c);
  }
  if (norm.some((c) => c.p.length > MAX_POINTS_PER_CUT)) return { ok: false, reason: 'too_long', message: COPY.too_long };
  if (pointCount(norm) > MAX_POINTS_TOTAL) return { ok: false, reason: 'too_much', message: COPY.too_much };
  return { ok: true, body: { name: cleanName(name), cuts: norm } };
}

// ── the plain "Carve" chip (the critic's fix: no emoji, Nina's 10-01 plain room buttons) ──
export const CARVE_CHIP = 'Carve';
/** The chip shows while the table is open (house.pumpkins.open) and you are at the patch or on the porch. */
export const carveChipHere = (rider, room) => !!(rider && rider.open === true) && (room === 'patch' || room === 'porch');
/** The room's chips with "Carve" leading them (once). Off = the SAME list back (null stays null), so every room
 *  without the table renders byte-identically. */
export function withCarveChip(verbs, on) {
  if (!on) return Array.isArray(verbs) && verbs.includes(CARVE_CHIP) ? verbs.filter((v) => v !== CARVE_CHIP) : verbs;
  return [CARVE_CHIP, ...(Array.isArray(verbs) ? verbs.filter((v) => v !== CARVE_CHIP) : [])];
}
